import { motion } from 'framer-motion';
import { PageTransition, StaggerContainer, StaggerItem } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { BorderCard } from '../components/BorderCard';
import { ExternalLink, Play } from 'lucide-react';

const accessories = [
  { 
    id: 'caps', 
    title: 'Caps', 
    description: 'Stylish headwear designed to complement casual outfits while providing everyday comfort.',
    link: 'https://www.canva.com/design/DAHDJfoWtus/OcC6dzb5VMtSkN3WM5HiVA/view?utm_content=DAHDJfoWtus&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=hf2136b3e89',
    videoLink: 'https://canva.link/ashpfj7msyee21u'
  },
  { 
    id: 'sunglasses', 
    title: 'Sunglasses', 
    description: 'Fashion-forward eyewear designed to enhance personal style while offering eye protection.',
    link: 'https://www.canva.com/design/DAHDJk_Qolk/2KHM08JCy0GQW3f6COEVUg/view?utm_content=DAHDJk_Qolk&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=hb83113c34e',
    videoLink: 'https://canva.link/xqj7bww41gew43w'
  },
  { 
    id: 'card-holder', 
    title: 'Card Holders', 
    description: 'Compact accessories designed to store essential cards in a sleek and minimal format.',
    link: 'https://www.canva.com/design/DAHDbTeAWZI/A2sukeTzKyCX8zL9fLeHOw/view?utm_content=DAHDbTeAWZI&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=he79cbbaa3b'
  },
  { 
    id: 'bags', 
    title: 'Bags', 
    description: 'Functional and stylish carry accessories designed for daily use and travel convenience.',
    link: 'https://www.canva.com/design/DAHDcl7ovh8/TGRyDp4L0HoPepURMEPOBw/view?utm_content=DAHDcl7ovh8&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h945dc80b2d'
  },
  { 
    id: 'bracelet-chain', 
    title: 'Bracelet & Chain', 
    description: 'Minimal accessories designed to subtly elevate personal style.',
    link: 'https://www.canva.com/design/DAHDKl29MLo/PKVHYA6FVx5ih-uqG9IXcA/view?utm_content=DAHDKl29MLo&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h27653a4a43'
  },
  { 
    id: 'belts', 
    title: 'Belts', 
    description: 'Essential accessories that combine functionality with refined styling.',
    link: 'https://www.canva.com/design/DAHDKMs91TM/4w_GtWHtP8tTOGbTekiBrQ/view?utm_content=DAHDKMs91TM&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=had68464d74'
  },
  { 
    id: 'socks', 
    title: 'Socks', 
    description: 'Comfortable everyday essentials designed for breathability and durability.',
    link: 'https://www.canva.com/design/DAHDbffB7rA/nc0bWlNsK7SaeVPdS0ARpA/view?utm_content=DAHDbffB7rA&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=hca6c73404b'
  },
  { 
    id: 'cufflinks', 
    title: 'Cufflinks', 
    description: 'Formal accessories designed to add a polished finishing touch to shirts.',
    link: 'https://www.canva.com/design/DAHDbUcbfeE/AswWrHAjMA-Oyww8cLDUHA/view?utm_content=DAHDbUcbfeE&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h8f711f5a3e'
  },
  { 
    id: 'perfumes', 
    title: 'Perfumes', 
    description: 'Premium fragrances crafted to enhance personal presence with long-lasting scents designed for everyday wear and special occasions.',
    link: 'https://canva.link/zker5m3itktobyd',
    videoLink: 'https://canva.link/g6v15cwr11fux0e'
  },
];

const AccessoryCard = ({ item }) => {
  return (
    <div className="block h-full" data-testid={`accessory-${item.id}`}>
      <BorderCard className="p-6 h-full group">
        <div className="flex flex-col h-full">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-sm uppercase tracking-[0.1em] font-semibold text-[#1A1A1A]">
              {item.title}
            </h3>
          </div>
          <p className="text-xs text-[#1A1A1A]/60 leading-relaxed flex-1">
            {item.description}
          </p>
          <div className="mt-4 pt-4 border-t border-[rgba(26,26,26,0.05)] space-y-3">
            <a 
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between hover:opacity-70 transition-opacity"
            >
              <span className="text-[10px] uppercase tracking-wider text-[#D32F2F] font-medium hover:underline">
                View Training Guide
              </span>
              <ExternalLink size={12} className="text-[#1A1A1A]/30" />
            </a>
            {item.videoLink && (
              <a 
                href={item.videoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between hover:opacity-70 transition-opacity"
              >
                <span className="text-[10px] uppercase tracking-wider text-[#1A1A1A] font-medium hover:underline flex items-center gap-2">
                  <Play size={10} className="text-[#D32F2F]" />
                  Training Video Access
                </span>
                <ExternalLink size={12} className="text-[#1A1A1A]/30" />
              </a>
            )}
          </div>
        </div>
      </BorderCard>
    </div>
  );
};

const AccessoriesPage = () => {
  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what/product" label="Back" />

      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Product Training
          </h2>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A]">
            Accessories
          </h1>
        </motion.div>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {accessories.map((item) => (
            <StaggerItem key={item.id}>
              <AccessoryCard item={item} />
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </PageTransition>
  );
};

export default AccessoriesPage;
